import { Outlet } from 'react-router-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import InteractiveTour, { TourHelpButton, useTour } from './InteractiveTour';
import { SupportModeBanner, SupportModeDialog, useSupportMode } from './SupportMode';
import { EstablishmentSelector } from './EstablishmentSelector';
import { PlanLimitsSummary } from './PlanLimits';
import { NotificationBell } from './NotificationBell';
import TrialBanner from './TrialBanner';
import MenuPreferencesDialog from './MenuPreferencesDialog';
import GuidedDemoPanel from './GuidedDemoPanel';
import { menuPreferencesAPI } from '../api';
import { 
  Heart, 
  House, 
  Stethoscope, 
  Users, 
  Gear, 
  SignOut,
  User,
  List,
  Pill,
  FirstAid,
  ClockCounterClockwise,
  PencilSimple,
  ChartLine,
  Clipboard,
  ClipboardText,
  Folders,
  Buildings,
  ShieldCheck,
  CaretDown,
  CaretRight,
  Heartbeat,
  GraduationCap,
  Question,
  FileText,
  ListChecks,
  Syringe,
  Package,
  CurrencyCircleDollar,
  Wrench,
  UploadSimple,
  Monitor,
  VideoCamera,
  SlidersHorizontal,
  ChatCircle,
  Cube
} from '@phosphor-icons/react';
import { Shield, Settings } from 'lucide-react';

/**
 * AppLayout - Layout principal unificado para todas las rutas privadas
 * 
 * Estructura:
 * AppLayout
 * ├── TrialBanner (condicional)
 * ├── SupportModeBanner (condicional)
 * ├── Sidebar (permanente)
 * ├── Header (permanente)
 * └── MainContent
 *     └── <Outlet /> (renderiza la página/módulo activo)
 * 
 * Cuando el usuario cambia de módulo, solo se actualiza el contenido central.
 * El menú lateral y el encabezado NO desaparecen ni se recargan.
 */
export default function AppLayout() {
  const { user, logout, isAdmin, isInstructor, isSuperAdmin, hasPermission } = useAuth();
  const { branding } = useBranding();
  const brandedLogo = branding?.branding_enabled && branding.logo_url ? branding.logo_url : null;
  const navigate = useNavigate();
  const location = useLocation();
  const [maintainersOpen, setMaintainersOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [difusionOpen, setDifusionOpen] = useState(false);
  const [saasOpen, setSaasOpen] = useState(false);
  const { isOpen: tourOpen, openTour, closeTour } = useTour();
  const { isSupportMode, supportInfo, exitSupportMode } = useSupportMode();
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [groupOrder, setGroupOrder] = useState(['main', 'docencia', 'mantenedores', 'organizacion', 'difusion', 'saas']);
  const [showMenuPrefs, setShowMenuPrefs] = useState(false);
  const [showGuidedDemo, setShowGuidedDemo] = useState(false);

  // Load menu preferences once on mount
  useEffect(() => {
    (async () => {
      try {
        const resp = await menuPreferencesAPI.get();
        if (resp.data?.group_order?.length) {
          setGroupOrder(resp.data.group_order);
        }
      } catch (e) {
        // Ignore — keep default order
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bug 1 fix: listen for `tour:open-group` events dispatched by the
  // Interactive Tour and expand the corresponding sidebar Collapsible so
  // its child `data-testid="nav-*"` items become mountable/queryable.
  useEffect(() => {
    const setters = {
      mantenedores: setMaintainersOpen,
      organizacion: setAdminOpen,
      difusion: setDifusionOpen,
      saas: setSaasOpen,
    };
    const handler = (e) => {
      const g = e?.detail?.group;
      const fn = g && setters[g];
      if (fn) fn(true);
    };
    window.addEventListener('tour:open-group', handler);
    return () => window.removeEventListener('tour:open-group', handler);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Verificar si es alumno (rol que tiene acceso más restringido)
  const isAlumno = user?.role === 'alumno';
  
  // Check if a menu item should be shown based on permissions
  const canView = (module) => {
    if (isSuperAdmin) return true;
    return hasPermission(module, 'ver');
  };

  // Para mantenedores: solo admins e instructores con permiso 'crear' o 'editar'
  const canAccessMaintainers = (module) => {
    if (isSuperAdmin || isAdmin) return true;
    if (isInstructor) return hasPermission(module, 'ver');
    return false;
  };

  // Para administración: solo admins y superadmins
  const canAccessAdmin = (module) => {
    if (isSuperAdmin) return true;
    if (module === 'carga_masiva') return isAdmin;
    if (isAdmin) return hasPermission(module, 'ver');
    return false;
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isInSubmenu = (paths) => paths.some(p => location.pathname.startsWith(p));

  // Main navigation items
  const mainNavItems = [
    { path: '/app/dashboard', label: 'Dashboard', icon: <House size={20} />, module: 'dashboard' },
    { path: '/app/scenarios', label: 'Escenarios', icon: <Stethoscope size={20} />, module: 'escenarios' },
    ...(isAlumno ? [] : [{ path: '/app/evaluations', label: 'Evaluaciones', icon: <Clipboard size={20} />, module: 'evaluaciones' }]),
    { path: '/app/history', label: 'Mi Historial', icon: <ChartLine size={20} />, module: 'reportes' },
    { path: '/app/xr/link', label: 'Vincular XR', icon: <Cube size={20} />, module: 'dashboard' },
  ];

  // Instructor menu items
  const instructorItems = (isInstructor || isAdmin || isSuperAdmin) ? [
    { path: '/app/instructor/my-students', label: 'Mis Estudiantes', icon: <Users size={18} />, module: 'estudiantes' },
    { path: '/app/instructor/supervision', label: 'Supervisión', icon: <Heartbeat size={18} />, module: 'supervision' },
    { path: '/app/instructor/supervision-history', label: 'Historial Supervisión', icon: <ChartLine size={18} />, module: 'supervision' },
    { path: '/app/instructor/courses', label: 'Cursos', icon: <GraduationCap size={18} />, module: 'cursos' },
  ] : [];

  // Mantenedores submenu (Clínicos + Config Establecimiento)
  const maintainerItems = isAlumno ? [] : [
    { path: '/app/admin/medications', label: 'Medicamentos', icon: <Pill size={18} />, module: 'medicamentos' },
    { path: '/app/admin/administration-routes', label: 'Vías Administración', icon: <Syringe size={18} />, module: 'medicamentos' },
    { path: '/app/admin/medication-presentations', label: 'Presentaciones', icon: <Package size={18} />, module: 'medicamentos' },
    { path: '/app/admin/actions', label: 'Acciones Clínicas', icon: <FirstAid size={18} />, module: 'acciones' },
    { path: '/app/admin/physiological-effects', label: 'Efectos Fisiológicos', icon: <Heartbeat size={18} />, module: 'medicamentos' },
    { path: '/app/admin/diagnoses', label: 'Diagnósticos', icon: <FileText size={18} />, module: 'diagnosticos' },
    { path: '/app/admin/treatment-plans', label: 'Planes Tratamiento', icon: <ListChecks size={18} />, module: 'tratamientos' },
    { path: '/app/admin/ecg-rhythms', label: 'Ritmos ECG', icon: <Heartbeat size={18} />, module: 'escenarios' },
    { path: '/app/admin/scenarios/catalog', label: 'Escenarios', icon: <Folders size={18} />, module: 'escenarios' },
    { path: '/app/admin/protocols', label: 'Protocolos', icon: <ClipboardText size={18} />, module: 'protocolos' },
    { path: '/app/admin/soft-skills-protocols', label: 'Protocolos H. Blandas', icon: <ChatCircle size={18} />, module: 'protocolos' },
    { path: '/app/admin/xr', label: 'SimuClinic XR', icon: <Cube size={18} />, module: 'protocolos' },
    { path: '/app/admin/xr/ranking', label: 'Ranking XR', icon: <Cube size={18} />, module: 'protocolos' },
    { path: '/app/admin/establishment-settings', label: 'Config. Establecimiento', icon: <Buildings size={18} />, module: 'instituciones' },
  ];

  // Organización (antes "Administración") - gobierno de la institución
  const adminItems = (isSuperAdmin || isAdmin) ? [
    { path: '/app/admin/establishments', label: 'Establecimientos', icon: <Buildings size={18} />, module: 'establecimientos' },
    { path: '/app/admin/users', label: 'Usuarios', icon: <Users size={18} />, module: 'usuarios' },
    { path: '/app/admin/roles', label: 'Roles y Permisos', icon: <ShieldCheck size={18} />, module: 'roles' },
    { path: '/app/admin/simulation-levels', label: 'Niveles Simulación', icon: <GraduationCap size={18} />, module: 'niveles' },
    { path: '/app/admin/reports', label: 'Reportes', icon: <ChartLine size={18} />, module: 'reportes' },
    { path: '/app/admin/audit', label: 'Auditoría', icon: <ClockCounterClockwise size={18} />, module: 'auditoria' },
  ] : [];

  // Difusión y Contenido - materiales institucionales/comerciales + carga masiva
  const difusionItems = (isSuperAdmin || isAdmin) ? [
    { path: '/app/admin/landing-demo', label: 'Demo Comercial', icon: <Monitor size={18} />, module: 'landing_demo' },
    { path: '/app/admin/videos', label: 'Videos Institucionales', icon: <VideoCamera size={18} />, module: 'videos_institucionales' },
    { path: '/app/admin/bulk-import', label: 'Carga Masiva', icon: <UploadSimple size={18} />, module: 'carga_masiva' },
  ] : [];

  // SaaS Panel - Solo para superadmins
  const saasItems = isSuperAdmin ? [
    { path: '/app/admin/plans', label: 'Planes SaaS', icon: <Package size={18} />, module: 'planes_saas' },
    { path: '/app/admin/commercial', label: 'Gestión Comercial', icon: <CurrencyCircleDollar size={18} />, module: 'comercial' },
    { path: '/app/admin/analytics', label: 'Analytics', icon: <ChartLine size={18} />, module: 'analytics' },
    { path: '/app/admin/settings', label: 'Configuración', icon: <Wrench size={18} />, module: 'configuracion' },
  ] : [];
  
  const showSupportButton = isSuperAdmin && !isSupportMode;

  const filteredMainNav = mainNavItems.filter(item => canView(item.module));
  const filteredMaintainers = maintainerItems.filter(item => canAccessMaintainers(item.module));
  const filteredAdmin = adminItems.filter(item => canAccessAdmin(item.module));
  const filteredDifusion = difusionItems.filter(item => canAccessAdmin(item.module));

  const showMaintainers = filteredMaintainers.length > 0 && !isAlumno;
  const showAdmin = filteredAdmin.length > 0 && (isSuperAdmin || isAdmin);
  const showDifusion = filteredDifusion.length > 0 && (isSuperAdmin || isAdmin);
  const showInstructor = instructorItems.length > 0;
  const showSaaS = saasItems.length > 0 && isSuperAdmin;

  // Auto-open submenus based on current path
  useEffect(() => {
    if (isInSubmenu(filteredMaintainers.map(i => i.path))) {
      setMaintainersOpen(true);
    }
    if (isInSubmenu(filteredAdmin.map(i => i.path))) {
      setAdminOpen(true);
    }
    if (isInSubmenu(filteredDifusion.map(i => i.path))) {
      setDifusionOpen(true);
    }
    if (isInSubmenu(saasItems.map(i => i.path))) {
      setSaasOpen(true);
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="app-layout">
      {/* Trial Banner - siempre visible cuando hay trial activo */}
      <TrialBanner />
      
      {/* Support Mode Banner */}
      {isSupportMode && (
        <SupportModeBanner supportInfo={supportInfo} onExit={exitSupportMode} />
      )}
      
      <div className={`flex flex-1 ${isSupportMode ? 'pt-12' : ''}`}>
        {/* Support Mode Dialog */}
        <SupportModeDialog 
          open={showSupportDialog} 
          onOpenChange={setShowSupportDialog}
          onStartSupport={() => {}}
        />
        
        {/* Sidebar - Desktop (PERMANENTE) */}
        <aside 
          className={`hidden lg:flex flex-col bg-white border-r border-slate-200 fixed h-full z-40 transition-all duration-300 ${
            sidebarCollapsed ? 'w-20' : 'w-80'
          } ${isSupportMode ? 'top-12' : ''}`}
          data-testid="sidebar"
        >
          {/* Logo */}
          <div className="h-40 flex items-center justify-center px-6 border-b border-slate-200">
            <Link to="/app/dashboard" className="flex items-center" data-testid="nav-logo">
              {sidebarCollapsed ? (
                <img src={brandedLogo || "/images/simuclinic-icon.png"} alt="Logo" className="w-10 h-10" style={{ objectFit: 'contain' }} />
              ) : brandedLogo ? (
                <div className="flex flex-col items-center gap-1" data-testid="branded-sidebar-logo">
                  <img src={brandedLogo} alt={branding.public_name || 'Logo'} className="max-h-20 w-auto max-w-[13rem]" style={{ objectFit: 'contain' }} />
                  {branding.public_name && <span className="text-sm font-semibold text-slate-700">{branding.public_name}</span>}
                  {branding.powered_by_simuclinic && <span className="text-[10px] text-slate-400">Powered by SimuClinic</span>}
                </div>
              ) : (
                <img src="/images/simuclinic-logo.png" alt="SimuClinic" className="w-56 h-auto" style={{ objectFit: 'contain' }} />
              )}
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3" data-testid="sidebar-nav">
            <div className="space-y-1">
              {(() => {
                // Group renderers keyed by group key
                const renderers = {
                  main: () => (
                    filteredMainNav.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                          isActive(item.path)
                            ? 'bg-blue-50 text-blue-600 font-medium'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        title={sidebarCollapsed ? item.label : undefined}
                      >
                        {item.icon}
                        {!sidebarCollapsed && <span>{item.label}</span>}
                      </Link>
                    ))
                  ),
                  docencia: () => (showInstructor ? (
                    <>
                      {!sidebarCollapsed && (
                        <div className="pt-4 pb-2">
                          <span className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            Docencia
                          </span>
                        </div>
                      )}
                      {instructorItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                            isActive(item.path)
                              ? 'bg-blue-50 text-blue-600 font-medium'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          title={sidebarCollapsed ? item.label : undefined}
                        >
                          {item.icon}
                          {!sidebarCollapsed && <span>{item.label}</span>}
                        </Link>
                      ))}
                    </>
                  ) : null),
                  mantenedores: () => (showMaintainers && !sidebarCollapsed ? (
                    <Collapsible open={maintainersOpen} onOpenChange={setMaintainersOpen}>
                      <CollapsibleTrigger className="w-full" data-tour="nav-mantenedores">
                        <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                          isInSubmenu(filteredMaintainers.map(i => i.path))
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}>
                          <div className="flex items-center gap-3">
                            <Gear size={20} />
                            <span>Mantenedores Clínicos</span>
                          </div>
                          {maintainersOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-200 pl-3">
                          {filteredMaintainers.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive(item.path)
                                  ? 'bg-blue-50 text-blue-600 font-medium'
                                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                              }`}
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.icon}
                              <span>{item.label}</span>
                            </Link>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : null),
                  organizacion: () => (showAdmin && !sidebarCollapsed ? (
                    <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
                      <CollapsibleTrigger className="w-full" data-tour="nav-organizacion">
                        <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                          isInSubmenu(filteredAdmin.map(i => i.path))
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}>
                          <div className="flex items-center gap-3">
                            <ShieldCheck size={20} />
                            <span>Organización</span>
                          </div>
                          {adminOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-200 pl-3">
                          {filteredAdmin.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive(item.path)
                                  ? 'bg-blue-50 text-blue-600 font-medium'
                                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                              }`}
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.icon}
                              <span>{item.label}</span>
                            </Link>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : null),
                  difusion: () => (showDifusion && !sidebarCollapsed ? (
                    <Collapsible open={difusionOpen} onOpenChange={setDifusionOpen}>
                      <CollapsibleTrigger className="w-full" data-tour="nav-difusion">
                        <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                          isInSubmenu(filteredDifusion.map(i => i.path))
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-slate-600 hover:bg-slate-100'
                        }`}>
                          <div className="flex items-center gap-3">
                            <Monitor size={20} />
                            <span>Difusión</span>
                          </div>
                          {difusionOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-4 mt-1 space-y-1 border-l-2 border-slate-200 pl-3">
                          {filteredDifusion.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive(item.path)
                                  ? 'bg-blue-50 text-blue-600 font-medium'
                                  : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                              }`}
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {item.icon}
                              <span>{item.label}</span>
                            </Link>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : null),
                  saas: () => (showSaaS && !sidebarCollapsed ? (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="px-3 text-xs font-semibold text-purple-600 uppercase tracking-wider mb-2">
                        Panel Global SaaS
                      </p>
                      <Collapsible open={saasOpen} onOpenChange={setSaasOpen}>
                        <CollapsibleTrigger className="w-full" data-tour="nav-saas">
                          <div className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                            isInSubmenu(saasItems.map(i => i.path))
                              ? 'bg-purple-50 text-purple-600'
                              : 'text-slate-600 hover:bg-purple-50 hover:text-purple-600'
                          }`}>
                            <div className="flex items-center gap-3">
                              <CurrencyCircleDollar size={20} />
                              <span>Gestión SaaS</span>
                            </div>
                            {saasOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="ml-4 mt-1 space-y-1 border-l-2 border-purple-200 pl-3">
                            {saasItems.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                  isActive(item.path)
                                    ? 'bg-purple-50 text-purple-600 font-medium'
                                    : 'text-slate-500 hover:bg-purple-50 hover:text-purple-600'
                                }`}
                                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                {item.icon}
                                <span>{item.label}</span>
                              </Link>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Support Mode Button */}
                      {showSupportButton && (
                        <button
                          onClick={() => setShowSupportDialog(true)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors w-full text-left text-slate-600 hover:bg-orange-50 hover:text-orange-600 mt-2"
                          data-testid="support-mode-btn"
                        >
                          <Shield size={18} />
                          <span>Modo Soporte</span>
                        </button>
                      )}
                    </div>
                  ) : null),
                };

                // Union of user's saved order + any missing default groups appended at end
                const defaultOrder = ['main', 'docencia', 'mantenedores', 'organizacion', 'difusion', 'saas'];
                const finalOrder = [
                  ...groupOrder.filter(k => renderers[k]),
                  ...defaultOrder.filter(k => !groupOrder.includes(k)),
                ];

                return finalOrder.map((key) => (
                  <div key={key} data-testid={`sidebar-group-${key}`}>
                    {renderers[key]?.()}
                  </div>
                ));
              })()}
            </div>
          </nav>

          {/* User Section */}
          <div className="p-3 border-t border-slate-200">
            <div className={`flex items-center gap-3 p-3 rounded-lg bg-slate-50 ${sidebarCollapsed ? 'justify-center' : ''}`}>
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <User size={20} className="text-blue-600" />
              </div>
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.nombre} {user?.apellido}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                  {user?.institution_name && (
                    <p className="text-xs text-slate-400 truncate">{user.institution_name}</p>
                  )}
                </div>
              )}
              {!sidebarCollapsed && (
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-red-500">
                  <SignOut size={18} />
                </Button>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-80'} ${isSupportMode ? 'pt-0' : ''}`}>
          {/* Top Bar - Mobile & Desktop (PERMANENTE) */}
          <header 
            className={`h-16 bg-white border-b border-slate-200 sticky ${isSupportMode ? 'top-12' : 'top-0'} z-30 flex items-center justify-between px-4 lg:px-8`}
            data-testid="app-header"
          >
            {/* Mobile: Logo + Menu */}
            <div className="flex items-center gap-3 lg:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                    <List size={24} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 z-[9999]">
                  <DropdownMenuLabel>Navegación</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {filteredMainNav.map((item) => (
                    <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}>
                      {item.icon}
                      <span className="ml-2">{item.label}</span>
                    </DropdownMenuItem>
                  ))}
                  {showInstructor && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs">Docencia</DropdownMenuLabel>
                      {instructorItems.map((item) => (
                        <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}>
                          {item.icon}
                          <span className="ml-2">{item.label}</span>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  {showMaintainers && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Gear size={18} />
                        <span className="ml-2">Mantenedores Clínicos</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {filteredMaintainers.map((item) => (
                            <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}>
                              {item.icon}
                              <span className="ml-2">{item.label}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  )}
                  {showAdmin && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <ShieldCheck size={18} />
                        <span className="ml-2">Organización</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {filteredAdmin.map((item) => (
                            <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}>
                              {item.icon}
                              <span className="ml-2">{item.label}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  )}
                  {showDifusion && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Monitor size={18} />
                        <span className="ml-2">Difusión</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {filteredDifusion.map((item) => (
                            <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}>
                              {item.icon}
                              <span className="ml-2">{item.label}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  )}
                  {showSaaS && (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <CurrencyCircleDollar size={18} />
                        <span className="ml-2">Panel SaaS</span>
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {saasItems.map((item) => (
                            <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)}>
                              {item.icon}
                              <span className="ml-2">{item.label}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/app/dashboard" className="flex items-center gap-2">
                <img src={brandedLogo || "/images/simuclinic-logo.png"} alt={branding?.public_name || 'SimuClinic'} className="h-9" style={{ objectFit: 'contain' }} />
              </Link>
            </div>

            {/* Desktop: Breadcrumb or Title area */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Establishment Selector */}
              {!isSuperAdmin && <EstablishmentSelector />}
              
              {/* Plan Limits Summary */}
              {(isAdmin || isSuperAdmin) && <PlanLimitsSummary />}
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              {/* Tour Help Button */}
              <TourHelpButton onClick={openTour} />
              
              {user?.status === 'pendiente' && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                  Cuenta pendiente de activación
                </span>
              )}
              
              {/* Notification Bell */}
              <NotificationBell />
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-btn" data-tour="user-menu">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <User size={18} className="text-blue-600" />
                    </div>
                    <span className="hidden sm:inline text-slate-700">{user?.nombre}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div>
                      <p className="font-medium">{user?.nombre} {user?.apellido}</p>
                      <p className="text-xs text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                      {user?.institution_name && (
                        <p className="text-xs text-slate-400">{user.institution_name}</p>
                      )}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/app/profile')} data-testid="menu-profile">
                    <User size={16} className="mr-2" />
                    Mi Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/app/history')} data-testid="menu-history">
                    <ClockCounterClockwise size={16} className="mr-2" />
                    Mi Historial
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openTour} data-testid="menu-tour">
                    <Question size={16} className="mr-2" />
                    Ver Tour de Ayuda
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowGuidedDemo(true)} data-testid="menu-guided-demo">
                    <Question size={16} className="mr-2" />
                    Iniciar Demo Guiada
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowMenuPrefs(true)} data-testid="menu-personalize">
                    <SlidersHorizontal size={16} className="mr-2" />
                    Personalizar menú
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <SignOut size={16} className="mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Main Content - AQUÍ SE RENDERIZA EL MÓDULO ACTIVO */}
          <main className={`p-4 lg:p-8 ${isSupportMode ? 'scroll-mt-28' : 'scroll-mt-16'}`} data-testid="main-content">
            <Outlet />
          </main>
        </div>
      </div>
      
      {/* Interactive Tour */}
      <InteractiveTour isOpen={tourOpen} onClose={closeTour} />

      {/* Menu Preferences Dialog */}
      <MenuPreferencesDialog
        open={showMenuPrefs}
        onOpenChange={setShowMenuPrefs}
        groups={[
          { key: 'main', label: 'Simulación (principal)' },
          ...(showInstructor ? [{ key: 'docencia', label: 'Docencia' }] : []),
          ...(showMaintainers ? [{ key: 'mantenedores', label: 'Mantenedores Clínicos' }] : []),
          ...(showAdmin ? [{ key: 'organizacion', label: 'Organización' }] : []),
          ...(showDifusion ? [{ key: 'difusion', label: 'Difusión' }] : []),
          ...(showSaaS ? [{ key: 'saas', label: 'Panel SaaS' }] : []),
        ]}
        initialOrder={groupOrder}
        onSaved={(newOrder) => setGroupOrder(newOrder)}
      />

      {/* Guided Demo Panel (shared alumno + instructor supervised session) */}
      <GuidedDemoPanel open={showGuidedDemo} onClose={() => setShowGuidedDemo(false)} />
    </div>
  );
}
